import { useEffect, useState, useContext } from 'react'
import Head from 'next/head'
import dynamic from 'next/dynamic'
import Header from './Header'
const Footer = dynamic(() => import('./Footer'))
const Messenger = dynamic(() => import('./Messenger'))

import MascotBox from '../common/MascotBox/MascotBox'
import { MuiPickersUtilsProvider } from '@material-ui/pickers'
import DayJsUtils from '@date-io/dayjs'
import Axios from 'axios'
import getConfig from 'next/config'
import Cookies from 'universal-cookie'
const PdpaPopup = dynamic(() => import('./PdpaPopup/PdpaPopup'))
import {
  renderTrackingAnalyticsScriptsHead,
  TrackingAnalyticsScriptsBody,
} from '../../components/common/TrackingAnalyticsScripts'
import PdpaUtility from '../utility/pdpaUtility'
import { useRouter } from 'next/router'

import { Context } from '../../context'
import { canonicalUrl, alternateUrls } from '../../utils/canonical'

const { publicRuntimeConfig } = getConfig()

const Layout = ({
  title = `SHIPPOP ส่งของ ส่งพัสดุ เช็คค่าส่ง จองขนส่ง ทำได้เลยทันที`,
  keywords = 'ขนส่ง, รวมขนส่ง, ส่งด่วน, ส่งของ, ส่งพัสดุ, เช็คค่าส่ง',
  description = 'เช็คค่าส่ง บริการขนส่ง ส่งพัสดุ จองขนส่งราคาถูก เรียกคนมารับของที่บ้าน พร้อมระบบติดตามพัสดุ เก็บเงินปลายทางโอนคืนไว ออกใบกำกับภาษีได้',
  favicon = '/favicon.ico',
  children,
  headEl,
  noFooter = false,
  noHeader = false,
  noFloatingButtons = false,
}) => {
  const Router = useRouter()
  const language = Router.locale

  const cookies = new Cookies()

  const { isFromApp } = useContext(Context)

  const [userData, setUserData] = useState(null)
  const [isPdpaShowing, setPdpaShowing] = useState(false)
  const [editCookiesMode, setEditCookiesMode] = useState(false)
  const [pdpaStates, setPdpaStates] = useState({
    isSetPdpa: false,
    allowTargeting: false,
    allowFunctionality: false,
    allowPerformance: false,
  })
  
  const setPdpaStateFromUtility = (
    { targeting, functionality, performance },
    isNotSet = false
  ) => {
    if (isNotSet) return
    setPdpaStates({
      isSetPdpa: true,
      allowTargeting: targeting,
      allowFunctionality: functionality,
      allowPerformance: performance,
    })
  }

  const setCheckSsoLoginCookie = () => {
    cookies.set('check_sso_login', 1, { maxAge: 2 * 60, path: '/' })
  }

  const redirectToLogin = () => {
    const currentUrl = encodeURIComponent(
      `${publicRuntimeConfig.siteDomain}${Router.asPath}`
    )
    window.location.replace(
      `${publicRuntimeConfig.memberService}/sso/login?cbUrl=${
        encodeURIComponent(
          `${publicRuntimeConfig.siteDomain}/verify?destUrl=`
        ) + currentUrl
      }&homePageUrl=${currentUrl}`
    )
  }

  useEffect(() => {
    const checkIsLoggedInInOtherServices = () => {
      setCheckSsoLoginCookie()
      redirectToLogin()
    }
    const checkIsLoggedOutOrChangeAccountInOtherServices = async () => {
      setCheckSsoLoginCookie()
      try {
        if (localStorage.getItem('jwt_token')) {
          // check is logged in
          const res = await Axios.post('/api/auth/is-sso-logged-in', {
            jwtToken: localStorage.getItem('jwt_token'),
          })
          const status = res.data.status
          if (status !== 'success') {
            setUserData(null)
            localStorage.removeItem('shp_user')
            localStorage.removeItem('jwt_token')
            redirectToLogin()
          }
        }
      } catch (err) {
        // do nothing
      }
    }

    // ******* ========== Load page flow (about member) =========== ************
    // when user load the page
    setUserData(JSON.parse(localStorage.getItem('shp_user')))
    if (
      !cookies.get('check_sso_login') &&
      !Router.asPath.includes('/checkprice') &&
      cookies.get('shippop_visited') === 'true'
    ) {
      // if it is the time to recheck login status
      // exclude /checkprice as it required to be redirected to from index when use checkprice form
      if (localStorage.getItem('jwt_token'))
        checkIsLoggedOutOrChangeAccountInOtherServices()
      else checkIsLoggedInInOtherServices()
    } else {
      // if not have to check login status
      const pdpaLocalStorage = PdpaUtility.getPdpaLocalStorage()
      const pdpaCookiesLogin = PdpaUtility.getPdpaCookies(true)
      const pdpaCookiesNoneLogin = PdpaUtility.getPdpaCookies(false)
      // case already login
      if (localStorage.getItem('jwt_token')) {
        if (pdpaLocalStorage) {
          PdpaUtility.setPdpaCookies(true, pdpaLocalStorage)
          setPdpaStateFromUtility(pdpaLocalStorage)
        } else {
          if (pdpaCookiesLogin) {
            PdpaUtility.setPdpaLocalStorage(pdpaCookiesLogin)
            setPdpaStateFromUtility(pdpaCookiesLogin)
            PdpaUtility.updatePdpaMemberService(pdpaCookiesLogin)
          } else {
            if (pdpaCookiesNoneLogin) {
              PdpaUtility.setPdpaLocalStorage(pdpaCookiesNoneLogin)
              PdpaUtility.setPdpaCookies(true, pdpaCookiesNoneLogin)
              setPdpaStateFromUtility(pdpaCookiesNoneLogin)
              PdpaUtility.updatePdpaMemberService(pdpaCookiesNoneLogin)
            } else {
              setPdpaStateFromUtility({}, true)
            }
          }
        }
      }
      // case not log in yet
      else {
        if (pdpaCookiesNoneLogin) {
          setPdpaStateFromUtility(pdpaCookiesNoneLogin)
        } else {
          setPdpaStateFromUtility({}, true)
        }
      }
    }

    // show pdpa popup if meet criteria
    if (
      !PdpaUtility.getPdpaByPriority().isSet &&
      publicRuntimeConfig.enablePdpa === 'true'
    ) {
      setPdpaShowing(true)
    }

    // set cookies that tell this browser have visited shippop before
    cookies.set('shippop_visited', 'true', { path: '/' })
  }, [])

  const setPdpa = ({ targeting, functionality, performance }) => {
    // set pdpa by save setting
    setPdpaStates({
      ...pdpaStates,
      allowTargeting: targeting,
      allowFunctionality: functionality,
      allowPerformance: performance,
    })

    if (localStorage.getItem('jwt_token')) {
      PdpaUtility.setPdpaLocalStorage({ targeting, functionality, performance })
      PdpaUtility.setPdpaCookies(true, {
        targeting,
        functionality,
        performance,
      })
      PdpaUtility.updatePdpaMemberService({
        targeting,
        functionality,
        performance,
      })
    } else {
      PdpaUtility.setPdpaCookies(false, {
        targeting,
        functionality,
        performance,
      })
    }
  }

  const handleClickSetCookies = () => {
    setPdpaShowing(true)
    setEditCookiesMode(true)
  }

  return (
    <>
      <MuiPickersUtilsProvider utils={DayJsUtils} locale={language}>
        <>
          <Head>
            <title>{title}</title>
            <link rel="icon" href={favicon} />
            <meta name="description" content={description} />
            <meta name="keywords" content={keywords} />
            <meta
              name="viewport"
              content="width=device-width, initial-scale=1.0"
            />
            <meta charSet="UTF-8" />
            {/* meta for Facebook thumbnails */}
            <meta
              key="og:title"
              property="og:title"
              content="SHIPPOP ส่งของ ส่งพัสดุ เปรียบเทียบราคา จองขนส่ง ทำได้เลยทันที"
            />
            <meta
              key="og:description"
              property="og:description"
              content="เปรียบเทียบราคาการส่งของ ส่งพัสดุ จองขนส่งและชำระค่าบริการออนไลน์ได้ทันที ที่สำคัญไม่ต้องออกจากบ้านเพราะเดียวขนส่งมารับถึงหน้าบ้านคุณ"
            />
            <meta
              key="og:image"
              property="og:image"
              content="/static/img/home/shippop_banner.jpg"
            />
            <meta key="og:url" property="og:url" content={canonicalUrl(Router)} />
            <meta key="og:site_name" property="og:site_name" content="SHIPPOP" />
            <meta key="og:locale" property="og:locale" content="th_TH" />
            <meta key="twitter:card" name="twitter:card" content="summary_large_image" />
            <meta key="twitter:title" name="twitter:title" content={title} />
            <meta
              key="twitter:description"
              name="twitter:description"
              content={description}
            />
            <link rel="canonical" key="canonical" href={canonicalUrl(Router)} />
            <link rel="alternate" hrefLang="th" href={alternateUrls(Router).th} />
            <link rel="alternate" hrefLang="en" href={alternateUrls(Router).en} />
            <link
              rel="alternate"
              hrefLang="x-default"
              href={alternateUrls(Router).th}
            />
            {headEl}
            {renderTrackingAnalyticsScriptsHead(pdpaStates)}
          </Head>
          <TrackingAnalyticsScriptsBody pdpaStates={pdpaStates} />
          {!noHeader && !isFromApp && <Header userData={userData?.user} />}
          {children}
          {!noFooter && (
            <Footer handleClickSetCookies={handleClickSetCookies} />
          )}
          {!noFloatingButtons && (
            <>
              {/* react-messenger-customer-chat renders a plain in-flow <div>
                  (its .fb-customerchat config node). Left in normal flow it
                  contributes layout height and produces a white strip below
                  the footer (~51px gap on /new). The real FB chat UI is a
                  fixed iframe the SDK appends to <body>, independent of this
                  node — so pinning the wrapper to a fixed, zero-size box takes
                  the config <div> out of document flow (footer sits flush)
                  without affecting the widget itself. */}
              <div
                style={{
                  position: 'fixed',
                  bottom: 0,
                  right: 0,
                  width: 0,
                  height: 0,
                }}
              >
                <Messenger />
              </div>
              {/* <BackToTopIcon /> */}
            </>
          )}
          <PdpaPopup
            isShowing={isPdpaShowing}
            setShowing={setPdpaShowing}
            pdpaStates={pdpaStates}
            setPdpa={setPdpa}
            isLoggedIn={
              typeof window !== 'undefined'
                ? localStorage.getItem('jwt_token')
                : false
            }
            editCookiesMode={editCookiesMode}
            setEditCookiesMode={setEditCookiesMode}
          />
          <MascotBox />
        </>
      </MuiPickersUtilsProvider>
    </>
  )
}

export default Layout
